import React, { useEffect, useState } from "react";
import { TrendUp, TrendDown, Warning, Target } from "@phosphor-icons/react";
import { fetchNiftyOptionsPredictor } from "../lib/api";

const EXPIRIES = [
  { id: "weekly", label: "Weekly" },
  { id: "monthly", label: "Monthly" },
];

const fmtInr = (v) =>
  v == null ? "—" : `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export default function OptionsPredictor() {
  const [expiry, setExpiry] = useState("weekly");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    fetchNiftyOptionsPredictor(expiry)
      .then((d) => !cancelled && setData(d))
      .catch((e) => !cancelled && setErr(e?.response?.data?.detail || "Failed to load options predictor"))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [expiry]);

  const rec = data?.recommendation;
  const isCall = rec?.option_type === "CALL";
  const dirColor = isCall ? "#34C759" : "#FF3B30";
  const DirIcon = isCall ? TrendUp : TrendDown;

  return (
    <section className="bg-surface border border-white/10 p-5 mb-6" data-testid="options-predictor">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Target size={18} weight="duotone" color="#FFCC00" />
          <h3 className="heading text-lg font-semibold">NIFTY Options Predictor</h3>
          <span className="overline px-2 py-0.5 border border-white/15 text-white/50" data-testid="options-simulated-badge">
            simulated estimate
          </span>
        </div>
        <div className="flex items-center gap-1">
          {EXPIRIES.map((e) => (
            <button
              key={e.id}
              onClick={() => setExpiry(e.id)}
              data-testid={`options-expiry-${e.id}`}
              className={`px-2.5 py-1 mono text-xs uppercase tracking-[0.18em] border transition-colors duration-150 ${
                expiry === e.id
                  ? "bg-white text-black border-white"
                  : "bg-transparent text-white/60 border-white/15 hover:border-white/40 hover:text-white"
              }`}
            >
              {e.label}
            </button>
          ))}
        </div>
      </div>

      {loading && !data ? (
        <div className="space-y-2" data-testid="options-loading">
          <div className="h-3 bg-white/5 w-full" />
          <div className="h-3 bg-white/5 w-10/12" />
          <div className="h-3 bg-white/5 w-8/12" />
        </div>
      ) : err ? (
        <div className="bg-bearish/10 border border-bearish/40 p-4 mono text-sm" data-testid="options-error">
          {err}
        </div>
      ) : rec ? (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-5">
            <div
              className="border p-4 flex flex-col items-center justify-center min-w-[180px]"
              style={{ borderColor: `${dirColor}55`, background: `${dirColor}0d` }}
              data-testid="options-recommendation-box"
            >
              <div className="flex items-center gap-2 mb-1">
                <DirIcon size={22} weight="bold" color={dirColor} />
                <span className="heading text-2xl font-bold" style={{ color: dirColor }} data-testid="options-type">
                  BUY {rec.option_type}
                </span>
              </div>
              <div className="overline mb-2" style={{ color: dirColor }}>{rec.bias}</div>
              <div className="mono text-3xl font-bold text-white" data-testid="options-strike">
                {rec.recommended_strike}
              </div>
              <div className="overline text-white/50">strike (NIFTY)</div>
              <div className="mono text-xl mt-2" data-testid="options-premium">
                {fmtInr(rec.estimated_premium)}
              </div>
              <div className="overline text-white/50">est. premium</div>
              {!rec.in_target_band && (
                <div className="flex items-center gap-1 mt-2 text-[10px] text-yellow-400" data-testid="options-band-warning">
                  <Warning size={12} weight="bold" />
                  <span>closest to ₹50-80 band</span>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 content-start">
              <div className="border border-white/5 px-3 py-2">
                <div className="overline">spot (NIFTY 50)</div>
                <div className="mono text-base text-white" data-testid="options-spot">{fmtInr(rec.spot)}</div>
              </div>
              <div className="border border-white/5 px-3 py-2">
                <div className="overline">sentiment score</div>
                <div className="mono text-base" data-testid="options-sentiment-score" style={{ color: rec.sentiment_score >= 55 ? "#34C759" : rec.sentiment_score <= 45 ? "#FF3B30" : "#FFCC00" }}>
                  {rec.sentiment_score}
                </div>
              </div>
              <div className="border border-white/5 px-3 py-2">
                <div className="overline">confidence</div>
                <div className="mono text-base text-white" data-testid="options-confidence">{rec.confidence}</div>
              </div>
              <div className="border border-white/5 px-3 py-2">
                <div className="overline">expiry</div>
                <div className="mono text-sm text-white" data-testid="options-expiry-date">{rec.expiry_date} ({rec.days_to_expiry}d)</div>
              </div>
              <div className="border border-white/5 px-3 py-2">
                <div className="overline">IV used</div>
                <div className="mono text-base text-white" data-testid="options-iv">{rec.iv_used_pct}%</div>
              </div>
              <div className="border border-white/5 px-3 py-2">
                <div className="overline">breakeven</div>
                <div className="mono text-base text-white" data-testid="options-breakeven">{fmtInr(rec.breakeven)}</div>
              </div>
            </div>
          </div>

          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-xs mono" data-testid="options-strikes-table">
              <thead>
                <tr className="text-white/40 border-b border-white/10">
                  <th className="text-left py-1.5 pr-4 font-normal overline">strike</th>
                  <th className="text-left py-1.5 pr-4 font-normal overline">otm pts</th>
                  <th className="text-left py-1.5 font-normal overline">est. premium</th>
                </tr>
              </thead>
              <tbody>
                {(rec.strikes_table || []).map((row) => {
                  const active = row.strike === rec.recommended_strike;
                  return (
                    <tr
                      key={row.strike}
                      className={`border-b border-white/5 ${active ? "bg-white/10" : ""}`}
                      data-testid={active ? "options-table-row-active" : "options-table-row"}
                    >
                      <td className="py-1.5 pr-4 text-white">{row.strike}</td>
                      <td className="py-1.5 pr-4 text-white/60">{row.otm_points}</td>
                      <td className={`py-1.5 ${active ? "text-white font-bold" : "text-white/80"}`}>{fmtInr(row.premium)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="overline text-white/40 mt-4" data-testid="options-disclaimer">
            {rec.disclaimer}
          </div>
        </>
      ) : null}
    </section>
  );
}
